import React, { useEffect, useState, forwardRef, useImperativeHandle, useRef } from "react";
import useApi from "../../hooks/useApi";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { PasswordSettingsDto } from "../../types/User";
import { getPasswordSettings } from "../../services/userService";
import { PASSWORDSYSTEMSETTINGS, SIGNUPMODE, COMMONCONSTANTS, VERIFICATIONTYPE } from "../../data/constants";
import { HelpOutline } from "@mui/icons-material";
import { List, ListItem, ListItemIcon, ListItemText, Tooltip } from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import { grey } from "@mui/material/colors";
import { POPOVERMESSAGES, USERSIGNUPMESSAGES } from "../../constants/messages";
import { IconButton } from "@mui/material";
import Button from "@mui/material/Button";
import OtpHandler from "./OtpVerificationComponent";
import Link from "@mui/material/Link";
import { Paper, Popper } from "@mui/material";
import "./signupwithpassword.scss";
interface SignUpWithPasswordProps {
  onPasswordInputChange: (password: string) => void;
  onConfirmPasswordInputChange: (password: string) => void;
  userFirstName: string;
  userLastName: string;
  userName: string;
  email: string;
  phone: string;
  otpSendTo: string;
  onOtpVerified: (otp: string, isOtpMatched: boolean) => void;
  authMode: string;
  onIsSessionLocked: () => void;
  onParentAction: (source?: string) => Promise<boolean>;
}
type PasswordComplexityRequirements = {
  U?: number; // Minimum number of uppercase letters
  L?: number; // Minimum number of lowercase letters
  N?: number; // Minimum number of numbers
  S?: number; // Minimum number of special characters
  Length?: number; // Minimum password length
  UserDetailsContains?: boolean; // Whether user details are in the password
  BlackList?: boolean; // Whether the password is in a blacklist
};

type PasswordComplexityRequirementsResult = {
  U?: boolean;
  L?: boolean;
  N?: boolean;
  S?: boolean;
  Length?: boolean;
  UserDetailsContains?: boolean;
  BlackList?: boolean;
  ProhibitedChars?: boolean;
};
export interface SignUpWithPasswordRef {
  childComponetCall: () => boolean;
  clearPassFieldchildComponetCall: () => boolean;
}

const SignUpWithPassword = forwardRef<SignUpWithPasswordRef, SignUpWithPasswordProps>(
  (
    {
      onPasswordInputChange,
      onConfirmPasswordInputChange,
      userFirstName,
      userLastName,
      userName,
      email,
      phone,
      otpSendTo,
      onOtpVerified,
      authMode,
      onIsSessionLocked,
      onParentAction,
    },
    ref
  ) => {
    SignUpWithPassword.displayName = "SignUpWithPassword";
    const [validationErrors, setValidationErrors] = useState({
      passwordError: false,
      confirmPasswordError: false,
      passwordErrorMessage: "",
      confirmPasswordErrorMessage: "",
    });
    const [passwordSettings, setPasswordSettings] = useState({
      blacklist: "",
      blacklistArray: [],
      complexityRequirements: "",
      prohibitedChars: "",
      length: 8,
    });
    const [passwordData, setPasswordData] = useState({
      password: "",
      confirmPassword: "",
      showPassword: false,
      showConfirmPassword: false,
      validationResults: {} as PasswordComplexityRequirementsResult,
    });
    const [openOTPDialog, setOpenOTPDialog] = React.useState(false);
    const otpHandlerRef = useRef(null);

    const { executeApi } = useApi();
    const updateState = <T extends Record<string, unknown>>(
      setState: React.Dispatch<React.SetStateAction<T>>
    ) => {
      return (newState: Partial<T>) => {
        setState((prevState) => ({
          ...prevState,
          ...newState,
        }));
      };
    };
    const updateValidationErrorsState = updateState<typeof validationErrors>(setValidationErrors);
    const updatePasswordSettingsState = updateState<typeof passwordSettings>(setPasswordSettings);
    const updatePasswordDataState = updateState<typeof passwordData>(setPasswordData);
    const paswordRef = useRef<HTMLInputElement>(null);
    const confirmPaswordRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      childComponetCall: () => {
        return validateBothPassword(passwordData.password, passwordData.confirmPassword);
      },
      clearPassFieldchildComponetCall: () => {        
       clearPasswordErrors();
       return true;
      },
    }));
    
    useEffect(() => {
      const fetchPasswordSettings = async () => {
        const settings = await executeApi<string, PasswordSettingsDto>(getPasswordSettings, "");
        if (settings) {
          const blacklist = Object.values(settings).find(
            (item) => item.property === PASSWORDSYSTEMSETTINGS.PASSWORD_BLACKLIST
          )?.value;
          const complexityRequirements = Object.values(settings).find(
            (item) => item.property === PASSWORDSYSTEMSETTINGS.PASSWORD_COMPLEXITY_REQUIREMENTS
          )?.value;
          const length = Object.values(settings).find(
            (item) => item.property === PASSWORDSYSTEMSETTINGS.PASSWORD_LENGTH
          )?.value;
          const prohibitedChars = Object.values(settings).find(
            (item) => item.property === PASSWORDSYSTEMSETTINGS.PROHIBITED_PASSWORD_CHAR
          )?.value;

          updatePasswordSettingsState({
            blacklist: blacklist,
            complexityRequirements: complexityRequirements,
            length: length,
            prohibitedChars: prohibitedChars,
            blacklistArray: blacklist.split(","),
          });
        }
      };
      fetchPasswordSettings();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClickShowPassword = () => {
      updatePasswordDataState({ showPassword: !passwordData.showPassword });
    };
    const handleClickShowConfirmPassword = () => {
      updatePasswordDataState({ showConfirmPassword: !passwordData.showConfirmPassword });
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {      
      const inputValue = e.target.value;
      if (inputValue.length >= 0) {
        updateValidationErrorsState({
          passwordError: false,
          passwordErrorMessage: "",
        });
      } else {
        updateValidationErrorsState({
          passwordError: true,
          passwordErrorMessage: USERSIGNUPMESSAGES.PASSWORD_IS_REQUIRED,
        });
      }
      onPasswordInputChange(inputValue);
      return (
        validatePasswordByComplexity(inputValue) ||
        validatePasswordMatch(inputValue, passwordData.confirmPassword)
      );
    };
    const validatePasswordByComplexity = (password: string) => {
      const rules = parseRule(passwordSettings.complexityRequirements);
      const results = validatePasswordByRule(password, rules);
      updatePasswordDataState({ password: password, validationResults: results });
      return !Object.values(results).some((value) => value === false);
    };

    const validatePasswordMatch = (pass: string, confirmPass: string) => {
      let isValid = false;
      if (pass !== confirmPass && confirmPass.length > 0) {
        updateValidationErrorsState({
          confirmPasswordError: true,
          confirmPasswordErrorMessage: USERSIGNUPMESSAGES.PASSWORD_DO_NOT_MATCH,
        });
        isValid = false;
      } else {
        updateValidationErrorsState({
          confirmPasswordError: false,
          confirmPasswordErrorMessage: "",
        });
        isValid = true;
      }
      return isValid;
    };
    const handleConfirmPasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newPassword = e.target.value;
      if (newPassword.length > 0) {
        updateValidationErrorsState({
          confirmPasswordError: false,
          confirmPasswordErrorMessage: "",
        });
      } else {
        updateValidationErrorsState({
          confirmPasswordError: true,
          confirmPasswordErrorMessage: USERSIGNUPMESSAGES.CONFIRM_PASSWORD_IS_REQUIRED,
        });
      }
      updatePasswordDataState({ confirmPassword: newPassword });
      onConfirmPasswordInputChange(newPassword);
      validateConfirmPassword(newPassword);
    };

    const validateBothPassword = (password: string, confirmPassword: string) => {
      focusOnEmptyField();
      const result =
        validatePasswordByComplexity(password) && validateConfirmPassword(confirmPassword);
      return result;
    };
    const clearPasswordErrors = () => {
      updatePasswordDataState({ password:""});
      updatePasswordDataState({ confirmPassword: "" });
      updateValidationErrorsState({
          passwordError: false,
          passwordErrorMessage: "",
          confirmPasswordError: false,
          confirmPasswordErrorMessage: ""
      });
    }
    const focusOnEmptyField = () => {      
      paswordRef.current?.focus();
      if (!passwordData.password) {
        updateValidationErrorsState({
          passwordError: true,
          passwordErrorMessage: USERSIGNUPMESSAGES.PASSWORD_IS_REQUIRED,
        });
        paswordRef.current?.focus();
      } else if (!passwordData.confirmPassword) {
        confirmPaswordRef.current?.focus();
      }
    };
    const validateConfirmPassword = (confirmPass: string) => {
      const isValidLength = confirmPass?.length > 0;
      updateValidationErrorsState({
        confirmPasswordError: !isValidLength,
        confirmPasswordErrorMessage: isValidLength
          ? ""
          : USERSIGNUPMESSAGES.CONFIRM_PASSWORD_IS_REQUIRED,
      });
      return isValidLength && validatePasswordMatch(passwordData.password, confirmPass);
    };

    const parseRule = (rule: string) => {
      const ruleRegex = /([ULNS])(\d+)/g;
      const requirements: Record<string, number> = {};
      let match;

      while ((match = ruleRegex.exec(rule)) !== null) {
        const [, type, count] = match;
        requirements[type] = parseInt(count, 10);
      }
      return requirements;
    };

    const validatePasswordByRule = (password: string, rules: PasswordComplexityRequirements) => {
      const results: PasswordComplexityRequirementsResult = {};
      if (rules.U) results.U = (password.match(/[A-Z]/g) || []).length >= rules.U;
      if (rules.L) results.L = (password.match(/[a-z]/g) || []).length >= rules.L;
      if (rules.N) results.N = (password.match(/[0-9]/g) || []).length >= rules.N;
      if (rules.S) results.S = (password.match(/[^A-Za-z0-9]/g) || []).length >= rules.S;
      if (passwordSettings?.length !== null) {
        results.Length = password.length >= passwordSettings.length;
      }
      if (userFirstName !== null || userLastName !== null) {
        results.UserDetailsContains = !validatePasswordContainsUserDetails(password);
      }
      if (password) {
        results.BlackList = validateRestrictedWords(password);
        const prohibitedRegex = createProhibitedRegex(passwordSettings.prohibitedChars);
        results.ProhibitedChars = !prohibitedRegex.test(password);
      }
      return results;
    };

    const validatePasswordContainsUserDetails = (password: string) => {
      if (
        [userFirstName, userLastName, userName]
          .filter((term) => term && term.trim()) // Remove empty or whitespace-only values
          .some((term) => password.toLowerCase().includes(term.toLowerCase()))
      ) {
        return true;
      }
      return false;
    };

    const validateRestrictedWords = (pass: string) => {
      for (const word of passwordSettings.blacklistArray) {
        const eachWord = String(word);
        if (pass.toLowerCase().includes(eachWord.toLowerCase())) {
          return false;
        }
      }
      return true;
    };
    const rules = parseRule(passwordSettings.complexityRequirements);

    const handleSendCode = async () => {
      if (!(await onParentAction(VERIFICATIONTYPE.PASSWORD))) return;
      if (!validateBothPassword(passwordData.password, passwordData.confirmPassword)) return;
      setOpenOTPDialog(true);
      if (otpHandlerRef.current) {
        otpHandlerRef.current.childComponetCall();
      }
    };
    const handleCloseDialog = () => {
      setOpenOTPDialog(false);
    };
    const onCheckOtpVerify = (otp: string, isVerified: boolean) => {
      onOtpVerified(otp, isVerified);
    };
    const handleIsSessionLocked = () => {
      onIsSessionLocked();
    };

    // PopOver
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const popperRef = useRef<HTMLDivElement>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (popperRef.current && !popperRef.current.contains(event.target as Node)) {
        setAnchorEl(null);
        popoveropensetAnchorEl(null);
      }
    };

    const handleClickOutsidePopOver = () => {
      setAnchorEl(null);
      popoveropensetAnchorEl(null);
    };

    useEffect(() => {
      if (anchorEl) {
        document.addEventListener(COMMONCONSTANTS.MOUSEDOWN, handleClickOutside);
      } else {
        document.removeEventListener(COMMONCONSTANTS.MOUSEDOWN, handleClickOutside);
      }
      return () => {
        document.removeEventListener(COMMONCONSTANTS.MOUSEDOWN, handleClickOutside);
      };
    }, [anchorEl]);

    const [popoveropenanchorEl, popoveropensetAnchorEl] = React.useState<HTMLButtonElement | null>(
      null
    );

    const popoveropen = Boolean(popoveropenanchorEl);
    const popoverid = popoveropen ? "dynamic-popper" : undefined;

    const popoveropenhandleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      popoveropensetAnchorEl(event.currentTarget);
    };

    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;
    const createProhibitedRegex = (chars: string) => {
      const arr = chars.split(",").map((c) => c.trim());
      const escaped = arr.map((c) => c.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
      return new RegExp(`[${escaped.join("")}]`);
    };
    return (
      <>
        <Typography component="h1" variant="h1" className="paper-title">
          {COMMONCONSTANTS.SETPASSWORD}
          <div className="popper-wrapper">
            {/* Clickable Icon */}
            <Tooltip title={POPOVERMESSAGES.HelpTooltip} arrow placement="top">
              <IconButton
                tabIndex={-1}
                aria-describedby={id}
                onClick={handleClick}
                sx={{ color: "#333333" }} // Apply custom icon color
              >
                {<HelpOutline sx={{ fontSize: "18px", fill: "#333333a6" }} />}
              </IconButton>
            </Tooltip>
            {/* Popover */}
            <Popper id={id} open={open} anchorEl={anchorEl} ref={popperRef}>
              <Paper
                sx={{
                  position: "relative",
                  p: 2,
                  boxShadow: 3,
                }}
              >
                {/* Arrow */}
                <div className="arrow" />
                {COMMONCONSTANTS.NEEDPASSWORD}
                <Link component="button" variant="body2" onClick={popoveropenhandleClick}>
                  {COMMONCONSTANTS.WHY}
                </Link>
              </Paper>
            </Popper>
            <Popper
              id={popoverid}
              open={popoveropen}
              anchorEl={popoveropenanchorEl}
              ref={popperRef}
            >
              <Paper
                sx={{
                  position: "relative",
                  p: 2,
                  boxShadow: 3,
                }}
              >
                {/* Arrow */}
                <div className="arrow" />
                <Typography variant="h1" component="h1" className="mb-3">
                  {COMMONCONSTANTS.WHYPASSWORD}
                </Typography>
                {COMMONCONSTANTS.UNAUTHORIZEDACCOUNT}
                <div>
                  {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
                  <button onClick={handleClickOutsidePopOver} autoFocus className="button">
                    {COMMONCONSTANTS.GOTIT}
                  </button>
                </div>
              </Paper>
            </Popper>
          </div>
        </Typography>
        <FormControl fullWidth>
          <TextField
            required
            fullWidth
            name="password"
            label="Password"
            type={passwordData.showPassword ? "text" : "password"}
            id="password"
            autoComplete="new-password"
            variant="outlined"
            error={validationErrors.passwordError}
            helperText={validationErrors.passwordErrorMessage}
            color={validationErrors.passwordError ? "error" : "primary"}
            value={passwordData.password}
            onChange={handlePasswordChange}
            inputRef={paswordRef}
            size="small"
            sx={{
              "& .MuiInputBase-root": { paddingRight: "6px" },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      passwordData.showPassword ? "hide the password" : "display the password"
                    }
                    onClick={handleClickShowPassword}
                    size="small"
                    tabIndex={-1}
                  >
                    {passwordData.showPassword ? (
                      <VisibilityOffOutlinedIcon fontSize="small" />
                    ) : (
                      <VisibilityOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {passwordData.password.length > 0 && (
            <List className="password-strength">
              <ListItem>
                <ListItemIcon>
                  <CheckCircle
                    fontSize="small"
                    sx={{
                      color: passwordData.validationResults.Length ? "success.main" : grey[300],
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`Be atleast ${passwordSettings.length} characters`}
                  sx={{
                    color: passwordData.validationResults.Length
                      ? "success.main"
                      : "text.secondary",
                  }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle
                    fontSize="small"
                    sx={{ color: passwordData.validationResults.L ? "success.main" : grey[300] }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`Contains ${rules.L} lower case letter`}
                  sx={{
                    color: passwordData.validationResults.U ? "success.main" : "text.secondary",
                  }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle
                    fontSize="small"
                    sx={{ color: passwordData.validationResults.U ? "success.main" : grey[300] }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`Contains ${rules.U} upper case letters`}
                  sx={{
                    color: passwordData.validationResults.U ? "success.main" : "text.secondary",
                  }}
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <CheckCircle
                    fontSize="small"
                    sx={{ color: passwordData.validationResults.N ? "success.main" : grey[300] }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`Contain ${rules.N} numbers`}
                  sx={{
                    color: passwordData.validationResults.N ? "success.main" : "text.secondary",
                  }}
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <CheckCircle
                    fontSize="small"
                    sx={{ color: passwordData.validationResults.S ? "success.main" : grey[300] }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`Contain ${rules.S} special characters`}
                  sx={{
                    color: passwordData.validationResults.S ? "success.main" : "text.secondary",
                  }}
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <CheckCircle
                    fontSize="small"
                    sx={{
                      color: passwordData.validationResults.UserDetailsContains
                        ? "success.main"
                        : grey[300],
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Not contain the First Name, Last Name, or Username"
                  sx={{
                    color: passwordData.validationResults.UserDetailsContains
                      ? "success.main"
                      : "text.secondary",
                  }}
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <CheckCircle
                    fontSize="small"
                    sx={{
                      color: passwordData.validationResults.BlackList ? "success.main" : grey[300],
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`Not contain - ${passwordSettings.blacklist.replace(/,/g, ", ")}`}
                  sx={{
                    color: passwordData.validationResults.BlackList
                      ? "success.main"
                      : "text.secondary",
                    wordWrap: "break-word",
                  }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle
                    fontSize="small"
                    sx={{
                      color: passwordData.validationResults.ProhibitedChars
                        ? "success.main"
                        : grey[300],
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`Not contain - ${passwordSettings.prohibitedChars}`}
                  sx={{
                    color: passwordData.validationResults.ProhibitedChars
                      ? "success.main"
                      : "text.secondary",
                    wordWrap: "break-word",
                  }}
                />
              </ListItem>
            </List>
          )}
        </FormControl>
        <FormControl fullWidth className="confirm-password-margin-top">
          <TextField
            required
            fullWidth
            name="password"
            label="Confirm Password"
            type={passwordData.showConfirmPassword ? "text" : "password"}
            id="confirmPassword"
            autoComplete="new-password"
            variant="outlined"
            value={passwordData.confirmPassword} 
            error={validationErrors.confirmPasswordError}
            helperText={validationErrors.confirmPasswordErrorMessage}
            color={validationErrors.confirmPasswordError ? "error" : "primary"}
            onChange={handleConfirmPasswordInputChange}
            size="small"
            inputRef={confirmPaswordRef}
            sx={{
              "& .MuiInputBase-root": { paddingRight: "6px" },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      passwordData.showPassword ? "hide the password" : "display the password"
                    }
                    onClick={handleClickShowConfirmPassword}
                    tabIndex={-1}
                  >
                    {passwordData.showConfirmPassword ? (
                      <VisibilityOffOutlinedIcon fontSize="small" />
                    ) : (
                      <VisibilityOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </FormControl>
        {authMode !== SIGNUPMODE.SIGNUP_WITH_PASSWORD_AND_AUTHAPP &&
          authMode !== SIGNUPMODE.SIGNUP_WITH_PASSWORD_AND_OTP && (
            <Button
              type="button"
              fullWidth
              className="btn-top-margin"
              variant="contained"
              onClick={handleSendCode}
              id="btnSendCode"
            >
              Send Code
            </Button>
          )}
        <div>
          <OtpHandler
            ref={otpHandlerRef}
            email={email}
            phone={phone}
            userName={userName}
            otpSendTo={otpSendTo}
            onOtpVerified={onCheckOtpVerify}
            onClose={handleCloseDialog}
            open={openOTPDialog}
            onIsSessionLocked={handleIsSessionLocked}
          />
        </div>
      </>
    );
  }
);

export default SignUpWithPassword;
